import React, { useState } from "react";
import {
  FolderIcon,
  MXIcon,
  ClockIcon,
  GetInTouch,
  CurriculumPlayIcon,
  CurriculumFileIcon,
} from "../svg";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClasses,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import formatDuration from "../container/helper/FormatDuration";
import formatFileSize from "../container/helper/FormatFileSize";

const CustomAccordion1 = styled(Accordion)(({ theme }) => ({
  border: "none",
  marginBottom: "10px",
  fontFamily: "figtree",
  boxShadow: "none",
  "&::before": {
    content: "none",
    display: "none",
  },
  position: "relative",
}));

const CustomAccordionSummary1 = styled((props) => (
  <AccordionSummary
    expandIcon={
      <ExpandMoreIcon
        sx={{
          fontSize: "20px",
          color: props.isExpanded ? "#1D83F8" : "#6E7485",
        }}
      />
    }
    {...props}
    sx={{
      display: "flex",
      alignItems: "start",
      position: "relative",
      "& .MuiAccordionSummary-content": {
        margin: "0 auto",
      },
      "& .MuiAccordionSummary-content.Mui-expanded": {
        margin: "0 10px",
      },
      "& .MuiButtonBase-root-MuiAccordionSummary-root.Mui-expanded": {
        minHeight: "36px",
      },
    }}
  />
))(({ theme }) => ({
  border: "none",
  padding: "0 16px",
  fontFamily: "figtree",
  flexDirection: "row",
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    order: -1,
    position: "absolute",
    bottom: "200",
    left: "0",
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(180deg)",
    },
}));

const CustomAccordionDetails1 = styled(AccordionDetails)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: 0,
  color: "rgba(101, 101, 103, 1)",
  fontFamily: "figtree",
}));

const ScrollableContainer = styled(Box)(({ theme }) => ({
  maxHeight: "calc(100vh - 300px)",
  overflowY: "auto",
  paddingRight: "8px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
}));

const CurriculumDetails = ({ curriculum, courseDetailsData }) => {
  const [expanded, setExpanded] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState({});

  const handleChange = (panelIndex) => (event, isExpanded) => {
    setExpanded(isExpanded ? panelIndex : false);
  };

  const toggleDescription = (index) => {
    setShowFullDescription((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Calculate the height needed to show exactly 10 items without scrolling
  const calculateContainerHeight = () => {
    // Estimate height per item (collapsed state)
    const itemHeight = 150; // Approximate height of a collapsed curriculum item
    return Math.min(itemHeight * 10, window.innerHeight - 200);
  };

  return (
    <div className="flex flex-col font-figtree">
      {/* Top section: Heading + box side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-[30px]">
        {/* Left: Heading - full width on mobile, 8 cols on desktop */}
        <div className="lg:col-span-8">
        <div className="font-[Figtree] font-semibold text-[55px] leading-[100%] tracking-[-0.03em] text-[#02054C] mb-16 sm:mb-32 lg:mb-64 text-left">
  Comprehensive Curriculum Structure
</div>
        </div>

        {/* Right: Info Box - full width on mobile, 4 cols on desktop */}
        <div className="lg:col-span-4">
          <div className="flex flex-col gap-5 bg-gray-50 p-6 rounded-xl shadow-sm">
            {/* Course Description */}
            <div className="font-[Figtree] font-medium text-[20px] leading-[26px] text-[#818285]">
              {courseDetailsData?.course?.curriculumDescription}
            </div>

            {/* Any Queries Button */}
           <button
  className="flex items-center gap-2 w-fit bg-blue-50 text-[#1D83F8] px-4 py-2 rounded-full hover:bg-blue-100 transition"
  onClick={() => {
    document
      .getElementById("enquire-now-form")
      .scrollIntoView({ behavior: "smooth" });
  }}
>
  <GetInTouch /> Any Queries ?
</button>

            {/* Course Stats */}
            <div className="flex flex-wrap gap-3 ">
              <span className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-[14px] font-normal leading-[22px] text-[#4E5566]">
                <FolderIcon /> {curriculum?.length} Sections
              </span>
              <span className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-[14px] font-normal leading-[22px] text-[#4E5566]">
                <MXIcon /> {courseDetailsData?.fileData?.totalLectures} lectures
              </span>
              <span className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-[14px] font-normal leading-[22px] text-[#4E5566]">
                <ClockIcon />
                {courseDetailsData?.course?.totalVideoDuration
                  ? formatDuration(
                      courseDetailsData?.course?.totalVideoDuration
                    )
                  : 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Curriculum list (full width on mobile, 8/12 centered on desktop) */}
      <div className="w-full lg:w-8/12 lg:mt-[-120px] mt-6">
        <div className="flex flex-col gap-6">
          {/* Always render all sections in a scrollable container sized to show exactly 10 items */}
          <ScrollableContainer sx={{ maxHeight: calculateContainerHeight() }}>
            {curriculum?.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 p-5 rounded-xl shadow-sm border border-gray-300 mb-4"
              >
                <CustomAccordion1
                  expanded={expanded === index}
                  onChange={handleChange(index)}
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    "& .MuiAccordionSummary-root": {
                      backgroundColor: "transparent",
                    },
                    "& .MuiAccordionDetails-root": {
                      backgroundColor: "transparent",
                    },
                    "& .MuiAccordionSummary-root.Mui-expanded": {
                      minHeight: "35px",
                    },
                  }}
                >
                  <CustomAccordionSummary1
                    aria-controls={`${index}-content`}
                    id={`${index}-header`}
                    isExpanded={expanded === index}
                    expandIcon={null}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      },
                    }}
                  >
                    {/* Left: Content */}
                    <div className="flex flex-col gap-4 w-full">
                      {/* Title */}
                      <div
                        className={`font-[Figtree] font-medium text-[25px] leading-[22px] ${
                          expanded === index ? "text-[#1D83F8]" : "text-[#1D2026]"
                        }`}
                      >
                        {item?.title}
                      </div>

                      {/* Stats in capsules */}
                      <div className="flex flex-wrap gap-3">
                        <span className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-[14px] text-[#4E5566]">
                          <MXIcon /> {item?.lectures?.length} lectures
                        </span>
                        <span className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-[14px] text-[#4E5566]">
                          <ClockIcon />
                          {item.totalModuleDuration
                            ? formatDuration(item.totalModuleDuration)
                            : 0}
                        </span>
                      </div>

                      {/* Description */}
                      <div
                        className={`font-[Figtree] font-normal text-[18px] leading-[130%] tracking-[-0.04em] text-[#1E1E1E] ${
                          expanded === index ? "block" : "line-clamp-3"
                        }`}
                      >
                        {item?.description}
                      </div>
                    </div>

                    {/* Right: Big blue arrow */}
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 cursor-pointer hover:bg-blue-100 transition">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`w-7 h-7 text-[#1D83F8] transition-transform duration-200 ${
                          expanded === index ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </CustomAccordionSummary1>

                  {/* Accordion Details */}
                  <CustomAccordionDetails1
                    sx={{ backgroundColor: "transparent" }}
                  >
                    <div className="flex flex-col gap-4">
                      {item?.lectures?.map((val, i) => (
                        <CustomAccordion1
                          key={i}
                          sx={{
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            "&:before": { display: "none" },
                            "& .MuiAccordionSummary-root": {
                              backgroundColor: "transparent",
                            },
                            "& .MuiAccordionDetails-root": {
                              backgroundColor: "transparent",
                            },
                            "& .MuiAccordionSummary-root.Mui-expanded": {
                              minHeight: "35px",
                            },
                          }}
                        >
                          <CustomAccordionSummary1
                            aria-controls={`panel${i}-content`}
                            id={`panel${i}-header`}
                          >
                            <div className="flex justify-between items-start w-full">
                              <div className="text-[14px] font-medium text-[#4E5566]">
                                {val.title}
                              </div>
                              <div className="flex items-center gap-1 text-[14px] font-medium text-[#4E5566]">
                                <ClockIcon />
                                {val.totalLectureDuration
                                  ? formatDuration(val.totalLectureDuration)
                                  : 0}
                              </div>
                            </div>
                          </CustomAccordionSummary1>

                          <CustomAccordionDetails1
                            sx={{ backgroundColor: "transparent" }}
                          >
                            <div className="flex flex-col gap-2 text-[14px] font-medium text-[#4E5566]">
                              <div className="flex items-center gap-2">
                                <CurriculumPlayIcon /> Video
                                <span>
                                  {val.totalLectureDuration
                                    ? formatDuration(val.totalLectureDuration)
                                    : 0}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <CurriculumFileIcon /> Material
                                <span>
                                  {val.attachFiles?.fileSize
                                    ? formatFileSize(val.attachFiles?.fileSize)
                                    : 0}
                                </span>
                              </div>
                            </div>
                          </CustomAccordionDetails1>
                        </CustomAccordion1>
                      ))}
                    </div>
                  </CustomAccordionDetails1>
                </CustomAccordion1>
              </div>
            ))}
          </ScrollableContainer>
        </div>
      </div>
    </div>
  );
};

export default CurriculumDetails;